import React, { useEffect, useState } from "react";
import { useLanguage } from "../../context/LanguageContext";
import { translations } from "../../i18n/translations";
import { FormValidationSummary, useFormValidation } from "../common/FormValidationSummary";
import type { FormStepProps } from "../../types/application";
import { isMobile } from "react-device-detect";
import { motion } from "framer-motion";
import "../../assets/gcweb-theme.css";
import "../../assets/gcweb-font-fix.css";

const AIConsentStep: React.FC<FormStepProps> = ({ data, onNext, onPrevious, onUpdate }) => {
  const { language } = useLanguage();
  const t = translations[language] || translations.en;
  const validation = useFormValidation({ t });
  const [AIConsentStatus, setConsentStatus] = useState(data.AIConsentStatus || "");

  useEffect(() => {
    setConsentStatus(data.AIConsentStatus || "");
  }, [data.AIConsentStatus]);

  const handleSubmit = (e: React.FormEvent) => {
    console.log("handleSubmit fired with ConsentStatus:", AIConsentStatus);
    validation.handleSubmit(e, () => {
      onUpdate({ AIConsentStatus: AIConsentStatus });
      onNext();
    });
  };

  if (isMobile)
    return (
      // <form
      //   className="wb-frmvld"
      //   onSubmit={handleSubmit}
      //   noValidate
      // >
      <motion.form
        ref={validation.formRef}
        className="wb-frmvld space-y-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        onSubmit={handleSubmit}
        onChangeCapture={validation.handleChangeCapture}
        noValidate
      >
        <FormValidationSummary
          t={t}
          errors={validation.errors}
          errorSummaryRef={validation.errorSummaryRef}
          formRef={validation.formRef}
        />
        {/* Section: AI Data Collection Consent */}
        <section className="alert alert-info">
          <h3 className="h5">{t.aiConsentLabel}</h3>
          <p className="mb-3">{t.aiConsentblab}</p>
          <ul>
            <li className="mb-3">{t.aiConsentul1}</li>
            <li>{t.aiConsentul2}</li>
          </ul>
        </section>

        {/* Confirmation Section */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {t.confirmation}
          </h2>
          <p className="text-lg text-gray-900">
            <strong>{t.pleaseIndicatePreference}</strong>
          </p>
        </div>
        
        <fieldset className="gc-chckbxrdio">
          <legend className="wb-inv">{t.confirmation}</legend>
          <ul className="list-unstyled lst-spcd-2">
            <li className="radio">
              <input
                type="radio"
                name="ConsentStatus"
                id="consent-yes"
                value="Yes"
                checked={AIConsentStatus === "Yes"}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setConsentStatus(e.target.value);
                }}
                required
              />
              <label htmlFor="consent-yes">{t.aiConsentYes}</label>
            </li>
            <li className="radio">
              <input
                type="radio"
                name="ConsentStatus"
                id="consent-no"
                value="No"
                checked={AIConsentStatus === "No"}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setConsentStatus(e.target.value);
                }}
                required
              />
              <label htmlFor="consent-no">{t.aiConsentNo}</label>
            </li>
          </ul>
        </fieldset>
        

        {/* Navigation */}
        <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onPrevious}
            className="w-full sm:w-auto px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t.previousButton}
          </button>
          <button
            type="submit"
            className="w-full sm:w-auto px-6 py-3 rounded-lg border-2 font-medium transition-colors primary-action-button"
          >
            {t.nextResumeUpload}
          </button>
        </div>
      </motion.form>
    );
  else
    return (
      // <form
      //   className="wb-frmvld"
      //   onSubmit={handleSubmit}
      //   noValidate
      // >
      <motion.form
        ref={validation.formRef}
        className="wb-frmvld space-y-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        onSubmit={handleSubmit}
        onChangeCapture={validation.handleChangeCapture}
        noValidate
      >
        <FormValidationSummary
          t={t}
          errors={validation.errors}
          errorSummaryRef={validation.errorSummaryRef}
          formRef={validation.formRef}
        />
        {/* Section: AI Data Collection Consent */}
        <section className="alert alert-info">
          <h3 className="h5">{t.aiConsentLabel}</h3>
          <p className="mb-3">{t.aiConsentblab}</p>
          <ul>
            <li className="mb-3">{t.aiConsentul1}</li>
            <li>{t.aiConsentul2}</li>
          </ul>
        </section>

        {/* Confirmation Section */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {t.confirmation}
          </h2>
          <p className="text-lg text-gray-900">
            <strong>{t.pleaseIndicatePreference}</strong>
          </p>
        </div>
        
        <fieldset className="gc-chckbxrdio">
          <legend className="wb-inv">{t.confirmation}</legend>
          <ul className="list-unstyled lst-spcd-2">
            <li className="radio">
              <input
                type="radio"
                name="ConsentStatus"
                id="consent-yes"
                value="Yes"
                checked={AIConsentStatus === "Yes"}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setConsentStatus(e.target.value)
                }
                required
              />
              <label htmlFor="consent-yes">{t.aiConsentYes}</label>
            </li>
            <li className="radio">
              <input
                type="radio"
                name="ConsentStatus"
                id="consent-no"
                value="No"
                checked={AIConsentStatus === "No"}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setConsentStatus(e.target.value)
                }
                required
              />
              <label htmlFor="consent-no">{t.aiConsentNo}</label>
            </li>
          </ul>
        </fieldset>

        {/* Navigation */}
        <div className="flex justify-between pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onPrevious}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t.previousButton}
          </button>
          <button
            type="submit"
            className="px-6 py-3 rounded-lg border-2 font-medium transition-colors primary-action-button"
          >
            {t.nextResumeUpload}
          </button>
        </div>
      </motion.form>
    );
};

export default AIConsentStep;
